import { EyeIcon, HeartIcon, TargetIcon, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { FunnelStage, FunnelStageValues } from '../../model/funnel';
import { formatPercent } from '../../model/format';

interface FunnelKpiCardsProps {
  kpis: FunnelStageValues;
}

interface StageConfig {
  stage: FunnelStage;
  icon: LucideIcon;
  iconBgClassName: string;
  accentClassName: string;
  labelKey: 'awareness' | 'consideration' | 'purchaseIntent';
  captionKey: 'topOfFunnel' | 'midFunnel' | 'bottomOfFunnel';
}

// Blue -> neutral -> green is a progression, not decoration: the funnel ends on
// green because purchase intent is the outcome worth reaching.
const STAGE_CONFIG: StageConfig[] = [
  {
    stage: 'awareness',
    icon: EyeIcon,
    iconBgClassName: 'bg-indigo-50 dark:bg-indigo-950',
    accentClassName: 'text-indigo-600 dark:text-indigo-300',
    labelKey: 'awareness',
    captionKey: 'topOfFunnel',
  },
  {
    stage: 'consideration',
    icon: HeartIcon,
    iconBgClassName: 'bg-surface dark:bg-surface-raised',
    accentClassName: 'text-foreground-alt',
    labelKey: 'consideration',
    captionKey: 'midFunnel',
  },
  {
    stage: 'purchaseIntent',
    icon: TargetIcon,
    iconBgClassName: 'bg-green-50 dark:bg-green-950',
    accentClassName: 'text-green-700 dark:text-green-300',
    labelKey: 'purchaseIntent',
    captionKey: 'bottomOfFunnel',
  },
];

export const FunnelKpiCards = ({ kpis }: FunnelKpiCardsProps) => {
  const { t } = useTranslation('analysis');

  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      {STAGE_CONFIG.map(
        ({ stage, icon: Icon, iconBgClassName, accentClassName, labelKey, captionKey }) => (
          <div
            key={stage}
            className="flex flex-col gap-1.5 rounded-lg border border-border-light bg-card p-xl"
          >
            {/* Icon then label, together on the left: the stage colour is carried by the
                icon tile, so the label reads as a label rather than a second accent. */}
            <div className="flex items-center gap-2.5">
              <div
                className={`flex size-[34px] shrink-0 items-center justify-center rounded-lg ${iconBgClassName}`}
              >
                <Icon className={`size-[17px] ${accentClassName}`} />
              </div>
              <span className="text-body-md font-medium text-foreground-alt">
                {t(`analysisResult.marketingPerformance.${labelKey}`)}
              </span>
            </div>
            <span className="text-[36px] font-medium leading-10 -tracking-[0.01em] text-foreground">
              {formatPercent(kpis[stage])}
            </span>
            <span className="text-xs text-muted-foreground">
              {t(`analysisResult.marketingPerformance.${captionKey}`)}
            </span>
          </div>
        ),
      )}
    </div>
  );
};
